import { formatInZone, instantToLocalParts } from '../../Utils/eventTime';

// Pulled out of Events.jsx (SCRUM-334) so the zone-aware label logic can be
// unit tested on its own — Events.jsx (and anything importing react-router-dom
// transitively) currently cannot be rendered under this repo's Jest config:
// react-router-dom@7.7.1's package.json `main` points at a `dist/main.js` that
// isn't in the published package, and jest-environment-jsdom's default
// `customExportConditions` skip the `exports` field fallback that would
// otherwise resolve it. Pre-existing, unrelated to this change.
//
// The Events card's "When" line: the app's weekday + time per end, plus the
// full date and the zone that operators need and parents don't:
//   "Sat, Sep 12, 2026, 8:00 AM – Sun, Sep 13, 2026, 6:00 PM CDT"
//   "Sat, Sep 12, 2026, 8:00 AM – 6:00 PM CDT"                (same local day)
//   "Sat, Sep 12, 2026 – Sun, Sep 13, 2026 · hours not set"  (SCRUM-334
//     backfill: local midnight → 23:59:59.999, i.e. nobody has entered real
//     hours yet — flag it rather than print "12:00 AM – 11:59 PM")
// null when the event hasn't got startsAt/timezone (an API that hasn't
// shipped SCRUM-334).
export function eventWhenLabel(eventData) {
  if (!eventData?.startsAt || !eventData?.timezone) return null;
  const { startsAt, endsAt, timezone } = eventData;
  const startDate = formatInZone(startsAt, timezone, 'date');
  if (!endsAt) return startDate;
  const endDate = formatInZone(endsAt, timezone, 'date');
  const sameDay = startDate === endDate;

  const { hm: startHm } = instantToLocalParts(startsAt, timezone);
  const { hm: endHm } = instantToLocalParts(endsAt, timezone);
  if (startHm === '00:00' && endHm === '23:59') {
    return `${sameDay ? startDate : `${startDate} – ${endDate}`} · hours not set`;
  }

  const startTime = timeOfDay(startsAt, timezone);
  // formatInZone 'time' ends in the zone abbreviation; the zone is stated once.
  const endTimeWithZone = formatInZone(endsAt, timezone, 'time');
  return sameDay
    ? `${startDate}, ${startTime} – ${endTimeWithZone}`
    : `${startDate}, ${startTime} – ${endDate}, ${endTimeWithZone}`;
}

function timeOfDay(iso, tz) {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz })
    .format(new Date(iso))
    .replace(/[\u202f\u00a0]/g, ' ');
}

// SCRUM-380: "Visible only to 2 accounts" for an allowlisted event, null for
// a public one (or an API that predates the field).
export function eventVisibilityLabel(eventData) {
  const n = Array.isArray(eventData?.allowedEmails) ? eventData.allowedEmails.length : 0;
  if (n === 0) return null;
  return `Visible only to ${n} account${n === 1 ? '' : 's'}`;
}

// "Sales until Tue, Sep 15, 2026, 11:59 PM CDT", or null pre-SCRUM-334.
export function eventSalesUntilLabel(eventData) {
  if (!eventData?.expiresAt || !eventData?.timezone) return null;
  return `Sales until ${formatInZone(eventData.expiresAt, eventData.timezone)}`;
}
